'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { Box } from 'foundations-components/transitional/components/Box';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { MoveableObject } from 'foundations-components/transitional/components/MoveableObject';
import { View } from 'foundations-components/transitional/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { AccordionItem } from 'foundations-components/transitional/components/AccordionItem';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { LockBadge } from 'foundations-components/transitional/components/LockBadge';
import { sanitize } from 'sanitize-text/sanitizers/Sanitize';
import { config } from 'sanitize-text/sanitizers/SanitizeConfiguration';
import { getPropertyObjectTypeId } from 'FormsUI/utils/PropertiesUtils';
import { trackEditorInteraction } from 'FormsUI/utils/TrackingUtils';
import { CommentableArea } from 'collaboration-sidebar';
import { DEPENDENT_FIELDS_MODULE_ID_POSTFIX, DEPENDENT_FIELD_MODULE_ID_AFFIX } from 'FormsUI/constants/Commenting';
import { CLICK_ADD_ANOTHER_DEPENDENT_RULE, CLICK_EDIT_DEPENDENT_RULE } from 'FormsUI/constants/TrackingInteractionKeys/Editor/FormTab';
import DependentFieldsConfigurationNewPanel from 'FormsUI/components/editor/form/configuration/DependentFieldsConfigurationNewPanel';
import { getDependentFieldOperatorLabel, DependentFieldPanelType } from 'FormsUI/constants/DependentFields';
import * as DependentFieldUtils from 'FormsUI/utils/DependentFields';
import { AddIcon } from 'foundations-assets/icons/components/AddIcon';
import { DeleteIcon } from 'foundations-assets/icons/components/DeleteIcon';
import { EditIcon } from 'foundations-assets/icons/components/EditIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export default class DependentFieldsConfiguration extends PureComponent {
  constructor(props) {
    super(props);
    const hasDependentFieldFilters = this.props.formField.dependentFieldFilters.length !== 0;
    this.state = {
      isDependentFieldConfigOpened: !hasDependentFieldFilters,
      dependentFieldConfigType: !hasDependentFieldFilters ? DependentFieldPanelType.NEW : null,
      dependentFieldConfigIndex: null
    };
    this.handleShowDependentFieldConfig = this.handleShowDependentFieldConfig.bind(this);
    this.handleAddDependentField = this.handleAddDependentField.bind(this);
    this.handleCancelDependentField = this.handleCancelDependentField.bind(this);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.formField.dependentFieldFilters.length !== 0 && this.props.formField.dependentFieldFilters.length === 0) {
      this.handleShowDependentFieldConfig();
    }
  }
  getLangKey(path) {
    return `editor.form.config.dependentFields.${path}`;
  }
  handleShowDependentFieldConfig() {
    this.setState({
      isDependentFieldConfigOpened: true,
      dependentFieldConfigType: DependentFieldPanelType.NEW,
      dependentFieldConfigIndex: null
    });
  }
  handleAddDependentField(newDependentFieldFilter) {
    const {
      formField,
      addDependentField
    } = this.props;
    addDependentField(formField, newDependentFieldFilter);
    this.handleCancelDependentField();
  }
  handleEditDependentField(dependentFieldFilterIndex, newDependentFieldFilter) {
    const {
      formField,
      editDependentField
    } = this.props;
    editDependentField(formField, dependentFieldFilterIndex, newDependentFieldFilter);
    this.handleCancelDependentField();
  }
  handleCancelDependentField() {
    this.setState({
      isDependentFieldConfigOpened: false,
      dependentFieldConfigType: null,
      dependentFieldConfigIndex: null
    });
  }
  handleDeleteDependentField(dependentFieldFilterIndex) {
    const {
      formField,
      removeDependentField
    } = this.props;
    removeDependentField(formField, dependentFieldFilterIndex);
  }
  handleClickDependentField(index) {
    trackEditorInteraction(CLICK_EDIT_DEPENDENT_RULE);
    this.setState({
      isDependentFieldConfigOpened: true,
      dependentFieldConfigType: DependentFieldPanelType.EDIT,
      dependentFieldConfigIndex: index
    });
  }
  maybeRenderDependentFieldsConfiguration() {
    const {
      numberFormFieldsWithinGroup
    } = this.props;
    if (numberFormFieldsWithinGroup > 1) {
      return /*#__PURE__*/_jsx(Alert, {
        marginBlockEnd: "400",
        type: "warning",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: this.getLangKey('disabledStateDescription')
        })
      });
    }
    return this.renderDependentFieldsConfiguration();
  }
  renderDependentFieldsConfiguration() {
    const {
      formField,
      mainAndQueuedFieldsByPropertyId,
      dependentFieldsByPropertyId,
      propertiesById,
      disableFields,
      userPreferredLocale
    } = this.props;
    const {
      dependentFieldConfigType,
      dependentFieldConfigIndex
    } = this.state;
    const dependentFieldFilters = formField.dependentFieldFilters.map((dependentFieldFilter, index) => {
      const {
        filters,
        dependentFormField
      } = dependentFieldFilter;
      const {
        operator
      } = filters[0];
      const previewValue = DependentFieldUtils.getPreviewValue(formField, filters[0]);
      return /*#__PURE__*/_jsx(View, {
        as: "div",
        paddingBlock: "100",
        children: /*#__PURE__*/_jsx(CommentableArea, {
          moduleId: `${getPropertyObjectTypeId(formField)}${DEPENDENT_FIELD_MODULE_ID_AFFIX}${index}`,
          description: {
            translationKey: 'formsDependentFieldWithLabel',
            options: {
              label: formField.label
            }
          },
          children: /*#__PURE__*/_jsxs(TFlex, {
            children: [/*#__PURE__*/_jsx(Box, {
              disabled: disableFields,
              children: /*#__PURE__*/_jsx(MoveableObject, {
                draggable: false,
                closeable: false,
                disabled: disableFields,
                children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                  "data-test-id": "rule-with-dependent-field",
                  disabled: disableFields,
                  message: 'editor.form.config.dependentFields.ruleWithDependentField',
                  options: {
                    formFieldLabel: sanitize(formField.label, config.TEXTONLY),
                    dependentFieldLabel: sanitize(dependentFormField.label, config.TEXTONLY),
                    operatorLabel: getDependentFieldOperatorLabel(operator),
                    previewValue: previewValue || ''
                  }
                })
              }, index)
            }), /*#__PURE__*/_jsx(Box, {
              marginInlineStart: "200",
              children: /*#__PURE__*/_jsx(IconButton, {
                disabled: disableFields,
                onClick: () => this.handleClickDependentField(index),
                "data-test-id": "edit-dependent-form-fields",
                children: /*#__PURE__*/_jsx(EditIcon, {})
              })
            }), /*#__PURE__*/_jsx(Box, {
              marginInlineStart: "200",
              children: /*#__PURE__*/_jsx(IconButton, {
                disabled: disableFields,
                onClick: () => this.handleDeleteDependentField(index),
                "data-test-id": "delete-dependent-form-fields",
                children: /*#__PURE__*/_jsx(DeleteIcon, {})
              })
            })]
          })
        })
      }, index);
    });
    if (dependentFieldConfigType === DependentFieldPanelType.EDIT) {
      const panel = /*#__PURE__*/_jsx(DependentFieldsConfigurationNewPanel, {
        type: DependentFieldPanelType.EDIT,
        totalDependentFields: dependentFieldFilters.length,
        initialDependentField: formField.dependentFieldFilters[dependentFieldConfigIndex],
        formField: formField,
        mainAndQueuedFieldsByPropertyId: mainAndQueuedFieldsByPropertyId,
        dependentFieldsByPropertyId: dependentFieldsByPropertyId,
        propertiesById: propertiesById,
        addDependentFieldFilter: this.handleAddDependentField,
        updateDependentFieldFilter: (...args) => this.handleEditDependentField(dependentFieldConfigIndex, ...args),
        cancelAddDependentFieldFilter: this.handleCancelDependentField,
        isFormLabelPropertyEnabled: this.props.isFormLabelPropertyEnabled,
        disableFields: this.props.disableFields,
        userPreferredLocale: userPreferredLocale
      });
      const dependentFieldFiltersWithPanel = dependentFieldFilters.slice(0, dependentFieldConfigIndex).concat([panel]).concat(dependentFieldFilters.slice(dependentFieldConfigIndex + 1));
      return /*#__PURE__*/_jsx("div", {
        children: dependentFieldFiltersWithPanel
      });
    } else if (dependentFieldConfigType === DependentFieldPanelType.NEW) {
      return /*#__PURE__*/_jsxs("div", {
        children: [dependentFieldFilters, /*#__PURE__*/_jsx(DependentFieldsConfigurationNewPanel, {
          type: DependentFieldPanelType.NEW,
          totalDependentFields: dependentFieldFilters.length,
          formField: formField,
          mainAndQueuedFieldsByPropertyId: mainAndQueuedFieldsByPropertyId,
          dependentFieldsByPropertyId: dependentFieldsByPropertyId,
          propertiesById: propertiesById,
          addDependentFieldFilter: this.handleAddDependentField,
          updateDependentFieldFilter: this.handleEditDependentField,
          cancelAddDependentFieldFilter: this.handleCancelDependentField,
          isFormLabelPropertyEnabled: this.props.isFormLabelPropertyEnabled,
          disableFields: this.props.disableFields
        })]
      });
    }
    return /*#__PURE__*/_jsxs("div", {
      children: [dependentFieldFilters, /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        children: /*#__PURE__*/_jsxs(Button, {
          use: "link",
          onClick: () => {
            this.handleShowDependentFieldConfig();
            trackEditorInteraction(CLICK_ADD_ANOTHER_DEPENDENT_RULE);
          },
          children: [/*#__PURE__*/_jsx(AddIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
            message: this.getLangKey('addCta')
          })]
        })
      })]
    });
  }
  render() {
    const {
      disableFields,
      formField
    } = this.props;
    return /*#__PURE__*/_jsx(CommentableArea, {
      moduleId: `${getPropertyObjectTypeId(formField)}${DEPENDENT_FIELDS_MODULE_ID_POSTFIX}`,
      description: {
        translationKey: 'formsDependentFields'
      },
      children: /*#__PURE__*/_jsxs(AccordionItem, {
        id: "dependent-fields-configuration",
        title: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: this.getLangKey('title')
          }), disableFields && /*#__PURE__*/_jsx(LockBadge, {})]
        }),
        defaultOpen: true,
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: this.getLangKey('description')
          })
        }), this.maybeRenderDependentFieldsConfiguration()]
      })
    });
  }
}
DependentFieldsConfiguration.propTypes = {
  formField: PropTypes.object.isRequired,
  mainAndQueuedFieldsByPropertyId: PropTypes.object.isRequired,
  dependentFieldsByPropertyId: PropTypes.object.isRequired,
  propertiesById: PropTypes.object.isRequired,
  addDependentField: PropTypes.func.isRequired,
  editDependentField: PropTypes.func.isRequired,
  removeDependentField: PropTypes.func.isRequired,
  numberFormFieldsWithinGroup: PropTypes.number.isRequired,
  isFormLabelPropertyEnabled: PropTypes.bool.isRequired,
  disableFields: PropTypes.bool,
  userPreferredLocale: PropTypes.string
};